import type { PracticeDifficulty } from "./practiceQuestionTemplates";
import { gradeSpanIncludes } from "./practiceGrades";
import type { PracticeLocale } from "./practiceLocale";
import type { PracticeBand } from "./practiceBands";
import type { PracticeSubject } from "./practiceSubjects";
import { GEOMETRY_STATISTICS_GENERATOR_CATALOG } from "./practiceGeometryStatisticsGenerators";
import { HIGH_SCHOOL_GENERATOR_CATALOG } from "./practiceHighSchoolGenerators";

/** Metadata for a parameterized exercise. The sampler lives in practiceGenerators. */
export type PracticeGeneratorMeta = {
  id: string;
  skillId: string;
  gradeMin: number;
  gradeMax: number;
  difficulty: PracticeDifficulty;
  subject: PracticeSubject;
  band: PracticeBand;
  name: { en: string; he: string };
  skillLabel: { en: string; he: string };
};

export const PRACTICE_GENERATOR_CATALOG: readonly PracticeGeneratorMeta[] = [
  meta("arithmetic.count-on", 0, 0, "intro", "Count on", "ספירה קדימה"),
  meta("arithmetic.add-within-5", 0, 0, "intro", "Add within 5", "חיבור עד 5"),
  meta("arithmetic.add-within-10", 1, 1, "intro", "Add within 10", "חיבור עד 10"),
  meta(
    "arithmetic.add-two-digit-no-carry",
    1,
    1,
    "core",
    "Add two-digit numbers",
    "חיבור מספרים דו-ספרתיים",
  ),
  meta("arithmetic.add-carry", 2, 2, "intro", "Add with carrying", "חיבור עם העברה"),
  meta(
    "arithmetic.add-carry-hundreds",
    3,
    3,
    "core",
    "Add hundreds with carrying",
    "חיבור מאות עם העברה",
  ),
  meta("arithmetic.sub-within-10", 1, 1, "intro", "Subtract within 10", "חיסור עד 10"),
  meta(
    "arithmetic.sub-two-digit-no-borrow",
    1,
    1,
    "core",
    "Subtract two-digit numbers",
    "חיסור מספרים דו-ספרתיים",
  ),
  meta(
    "arithmetic.sub-borrow",
    2,
    2,
    "intro",
    "Subtract with regrouping",
    "חיסור עם פריטה",
  ),
  meta(
    "arithmetic.sub-borrow-hundreds",
    3,
    3,
    "core",
    "Subtract hundreds with regrouping",
    "חיסור מאות עם פריטה",
  ),
  meta("arithmetic.place-tens", 1, 1, "intro", "Tens place value", "ערך ספרת העשרות"),
  meta(
    "arithmetic.place-hundreds",
    2,
    3,
    "core",
    "Hundreds place value",
    "ערך ספרת המאות",
  ),
  meta(
    "arithmetic.multiply-easy",
    3,
    3,
    "intro",
    "Multiplication by 2, 5, and 10",
    "כפל ב-2, ב-5 וב-10",
  ),
  meta(
    "arithmetic.multiply-core",
    3,
    4,
    "core",
    "Multiplication facts",
    "עובדות כפל",
  ),
  meta(
    "arithmetic.multiply-hard",
    4,
    4,
    "challenge",
    "Harder multiplication facts",
    "עובדות כפל מתקדמות",
  ),
  meta("arithmetic.divide-exact", 3, 3, "intro", "Exact division", "חילוק מדויק"),
  meta("arithmetic.divide-exact-core", 4, 4, "core", "Division facts", "עובדות חילוק"),
  meta(
    "arithmetic.divide-remainder",
    4,
    4,
    "challenge",
    "Division with a remainder",
    "חילוק עם שארית",
  ),
  meta(
    "arithmetic.compare-fractions-same-den",
    3,
    3,
    "intro",
    "Compare fractions",
    "השוואת שברים",
  ),
  meta(
    "arithmetic.compare-fractions-same-num",
    4,
    4,
    "core",
    "Compare fractions with the same numerator",
    "השוואת שברים עם מונה שווה",
  ),
  meta("arithmetic.add-fractions-same-den", 4, 4, "intro", "Add fractions", "חיבור שברים"),
  meta(
    "arithmetic.add-fractions-related",
    5,
    5,
    "core",
    "Add fractions with related denominators",
    "חיבור שברים עם מכנים קשורים",
  ),
  meta(
    "arithmetic.equivalent-fractions",
    3,
    3,
    "intro",
    "Equivalent fractions",
    "שברים שקולים",
  ),
  meta(
    "arithmetic.equivalent-missing",
    4,
    4,
    "core",
    "Missing numerator",
    "מונה חסר",
  ),
  meta(
    "arithmetic.decimals-compare",
    4,
    5,
    "intro",
    "Compare decimals",
    "השוואת מספרים עשרוניים",
  ),
  meta(
    "arithmetic.decimals-add",
    5,
    6,
    "core",
    "Add decimals",
    "חיבור מספרים עשרוניים",
  ),
  meta("arithmetic.percent-simple", 5, 5, "intro", "Half of a number", "חצי ממספר"),
  meta(
    "arithmetic.percent-simple-core",
    6,
    6,
    "core",
    "Ten percent and twenty-five percent",
    "עשרה אחוזים ורבע",
  ),
  ...HIGH_SCHOOL_GENERATOR_CATALOG,
  ...GEOMETRY_STATISTICS_GENERATOR_CATALOG,
];

function meta(
  id: string,
  gradeMin: number,
  gradeMax: number,
  difficulty: PracticeDifficulty,
  en: string,
  he: string,
  subject: PracticeSubject = "General",
  band: PracticeBand = "elementary",
): PracticeGeneratorMeta {
  return {
    id,
    skillId: id,
    gradeMin,
    gradeMax,
    difficulty,
    subject,
    band,
    name: { en, he },
    skillLabel: { en, he },
  };
}

export function getPracticeGeneratorForSkill(
  skillId: string,
): PracticeGeneratorMeta | undefined {
  return PRACTICE_GENERATOR_CATALOG.find((row) => row.skillId === skillId);
}

export function getPracticeGeneratorMeta(
  id: string,
): PracticeGeneratorMeta | undefined {
  return PRACTICE_GENERATOR_CATALOG.find((row) => row.id === id);
}

export function generatorMatchesGrade(
  row: Pick<PracticeGeneratorMeta, "gradeMin" | "gradeMax">,
  grade: number | null | undefined,
): boolean {
  if (grade == null) return true;
  return gradeSpanIncludes(grade, row.gradeMin, row.gradeMax);
}

export function practiceGeneratorsFor(
  band: string | null | undefined,
  subject: string | null | undefined,
  grade: number | null | undefined,
): PracticeGeneratorMeta[] {
  return PRACTICE_GENERATOR_CATALOG.filter((row) => {
    if (band && row.band !== band) return false;
    if (subject && row.subject !== subject) return false;
    return generatorMatchesGrade(row, grade);
  });
}

export function generatorDisplayName(
  row: PracticeGeneratorMeta,
  locale: PracticeLocale,
): string {
  return row.name[locale];
}
